import { COLOR } from "../config.ts";

/**
 * A Hazard is the unified danger entity for the evasion phases. It is either a
 * `circle` (a bullet / geometric primitive) or a `rect` (a sweeping bar / beam /
 * wall, optionally rotated). Both support an optional telegraph: while
 * `warnT > 0` the hazard is shown as a harmless warning outline; once it elapses
 * the hazard becomes lethal. This drives the "danger is telegraphed well ahead
 * of impact" pillar from the design doc.
 *
 * Hazards are pooled, so `reset()` fully reinitializes a recycled instance.
 */
export type HazardKind = 0 | 1; // 0 = circle, 1 = rect
export const HZ_CIRCLE: HazardKind = 0;
export const HZ_RECT: HazardKind = 1;

export class Hazard {
  kind: HazardKind = HZ_CIRCLE;

  // Position (current) and previous-step position (for render interpolation).
  x = 0;
  y = 0;
  px = 0;
  py = 0;
  vx = 0;
  vy = 0;

  // circle: radius. rect: half-extents hw/hh and rotation angle.
  radius = 8;
  hw = 8;
  hh = 8;
  angle = 0;
  spin = 0; // angular velocity (rad/s)

  warnT = 0; // remaining telegraph time (s); harmless while > 0
  warnTotal = 0; // original telegraph duration (for warning animation)
  life = 999; // remaining lethal lifetime (s) before auto-despawn
  fade = 0; // visual spawn/despawn fade [0..1] ramps to 1
  color = COLOR.hazard;
  // If true, despawns when fully outside the arena (moving bullets/bars).
  cullOffscreen = true;
  grazed = false; // has the player already grazed this hazard?
  ay = 0; // vertical acceleration (ballistic bombs)
  // If > 0, expiring by `life` detonates into this many bullets (bombs).
  burstOnExpire = 0;
  burstSpeed = 240;

  get active(): boolean {
    return this.warnT <= 0;
  }

  resetCircle(
    x: number,
    y: number,
    vx: number,
    vy: number,
    radius: number,
    opts?: Partial<Hazard>,
  ): void {
    this.kind = HZ_CIRCLE;
    this.x = this.px = x;
    this.y = this.py = y;
    this.vx = vx;
    this.vy = vy;
    this.radius = radius;
    this.angle = 0;
    this.spin = 0;
    this.warnT = this.warnTotal = 0;
    this.life = 999;
    this.fade = 0;
    this.color = COLOR.bullet;
    this.cullOffscreen = true;
    this.grazed = false;
    this.ay = 0;
    this.burstOnExpire = 0;
    this.burstSpeed = 240;
    if (opts) Object.assign(this, opts);
  }

  resetRect(
    x: number,
    y: number,
    hw: number,
    hh: number,
    angle: number,
    opts?: Partial<Hazard>,
  ): void {
    this.kind = HZ_RECT;
    this.x = this.px = x;
    this.y = this.py = y;
    this.vx = 0;
    this.vy = 0;
    this.hw = hw;
    this.hh = hh;
    this.angle = angle;
    this.spin = 0;
    this.warnT = this.warnTotal = 0;
    this.life = 999;
    this.fade = 0;
    this.color = COLOR.hazard;
    this.cullOffscreen = true;
    this.grazed = false;
    this.ay = 0;
    this.burstOnExpire = 0;
    this.burstSpeed = 240;
    if (opts) Object.assign(this, opts);
  }
}
